// Third-party
import React, { CSSProperties } from 'react';
import classNames from 'classnames';

// Assets
import { ReactComponent as CapIntelLogo } from '../../../../images/proposal-logo.svg?react';
import { ReactComponent as ZigZagPattern } from '../../../../images/patterns/zigzag.svg?react';
import { ReactComponent as LinesPattern } from '../../../../images/patterns/lines.svg?react';
import { ReactComponent as RadialPattern } from '../../../../images/patterns/radial.svg?react';
import { ReactComponent as EqualizerPattern } from '../../../../images/patterns/equalizer.svg?react';

// Utils
import { FormattedMessage, formatLocalizedString } from '../../../../utils/i18n';
import colorUtils from '../../../../utils/colors';
import { PROPOSAL_ORIENTATION, PROPOSAL_PATTERN } from '../../utils/constants';

// Namespaces
import * as ProposalNS from '../../Proposal';

import Dictionary from '@/types/dictionary';
import Nullable from '@/types/nullable';
import ProposalOrientation from '@/types/proposal-orientation';
import LocalizedString from '@/types/localized-string';
import Locale from '@/types/locale';

type Props = {
  locale?: Locale;
  orientation: ProposalOrientation;
  bannerStyles?: ProposalNS.ProposalStyleBanners;
  customDisclaimer?: LocalizedString;
  extraDisclaimers?: LocalizedString[];
};

const BACKGROUND_PATTERN: Dictionary<Nullable<React.FunctionComponent<React.SVGProps<SVGSVGElement>>>> = {
  [PROPOSAL_PATTERN.ZIGZAG]: ZigZagPattern,
  [PROPOSAL_PATTERN.LINES]: LinesPattern,
  [PROPOSAL_PATTERN.RADIAL]: RadialPattern,
  [PROPOSAL_PATTERN.EQUALIZER]: EqualizerPattern,
  [PROPOSAL_PATTERN.NONE]: null,
};

const ProposalFooterComponent = ({ locale, orientation, bannerStyles, customDisclaimer, extraDisclaimers }: Props) => {
  let style: CSSProperties = {};
  let BackgroundPattern;
  let patternColor;

  if (bannerStyles) {
    const { background, border, color, pattern, gradientRatio } = bannerStyles;
    patternColor = bannerStyles.patternColor;

    style = {
      background: colorUtils.getCssGradient(background, gradientRatio),
      borderBottom: border && `1px solid ${border}`,
      color,
    };

    // TODO -- CON-1286: Set pattern color based on background color's luminance
    BackgroundPattern = BACKGROUND_PATTERN[pattern || PROPOSAL_PATTERN.ZIGZAG];
  }

  // Added class for CIBC styles
  const classes = classNames('proposal-footer-component', {
    landscape: orientation && orientation === PROPOSAL_ORIENTATION.LANDSCAPE,
  });

  return (
    <div className={classes} style={style}>
      <div className="disclaimer" data-private data-dd-privacy="mask">
        {customDisclaimer === undefined && (
          <>
            <p>
              <FormattedMessage locale={locale} id="Proposal.FooterDisclaimer.1" />
            </p>
            <p>
              <FormattedMessage locale={locale} id="Proposal.FooterDisclaimer.2" />
            </p>
            <p>
              <FormattedMessage locale={locale} id="Proposal.FooterDisclaimer.3" />
            </p>

            {extraDisclaimers &&
              extraDisclaimers.map((disclaimer, index) => <p key={`footer-disclaimer-${index}`}>{formatLocalizedString(disclaimer, locale)}</p>)}
          </>
        )}

        {customDisclaimer !== undefined && (
          <p>
            <span>{formatLocalizedString(customDisclaimer, locale)}</span>
          </p>
        )}
      </div>

      <div className="powered-by d-flex flex-column">
        <FormattedMessage locale={locale} id="Proposal.PoweredBy" />
        <CapIntelLogo className="capintel-logo" />
      </div>

      {BackgroundPattern != null && <BackgroundPattern stroke={patternColor} className="proposal-pattern" />}
    </div>
  );
};

export default ProposalFooterComponent;
